import { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { API } from "../App";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import Sidebar from "../components/Sidebar";
import { Calendar, Bell, ListChecks, Phone, CaretLeft, CaretRight, Note as NoteIcon } from "@phosphor-icons/react";

const TYPE_STYLE = {
  task: { bg: "bg-blue-50", text: "text-blue-700", border: "border-blue-200", Icon: ListChecks, label: "Task" },
  note: { bg: "bg-amber-50", text: "text-amber-700", border: "border-amber-200", Icon: NoteIcon, label: "Reminder" },
  meeting: { bg: "bg-green-50", text: "text-green-700", border: "border-green-200", Icon: Phone, label: "Meeting" },
};

const PRIORITY_BAR = { P0: "#DC2626", P1: "#F59E0B", P2: "#002FA7", P3: "#64748B" };

const pad = (n) => String(n).padStart(2, "0");

const buildMonthGrid = (year, month) => {
  // month: 1-12
  const first = new Date(year, month - 1, 1);
  const firstWeekday = first.getDay(); // 0=Sun
  const daysInMonth = new Date(year, month, 0).getDate();
  const cells = [];
  // Leading blanks (prev month)
  for (let i = 0; i < firstWeekday; i++) {
    const d = new Date(year, month - 1, -firstWeekday + i + 1);
    cells.push({ date: d, inMonth: false });
  }
  for (let d = 1; d <= daysInMonth; d++) {
    cells.push({ date: new Date(year, month - 1, d), inMonth: true });
  }
  while (cells.length % 7 !== 0) {
    const last = cells[cells.length - 1].date;
    const d = new Date(last);
    d.setDate(d.getDate() + 1);
    cells.push({ date: d, inMonth: false });
  }
  return cells;
};

const Alerts = ({ user, logout }) => {
  const today = new Date();
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth() + 1); // 1-12
  const [byDate, setByDate] = useState({});
  const [totals, setTotals] = useState({ tasks: 0, notes: 0, meetings: 0 });
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(null);
  const [filters, setFilters] = useState({ task: true, note: true, meeting: true });

  const monthStr = `${year}-${pad(month)}`;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        user_id: user.user_id,
        month: monthStr,
        requester_role: user.role,
        requester_name: user.name,
      });
      const { data } = await axios.get(`${API}/alerts/calendar?${params}`);
      setByDate(data.by_date || {});
      setTotals(data.totals || { tasks: 0, notes: 0, meetings: 0 });
    } catch { toast.error("Failed to load calendar"); }
    finally { setLoading(false); }
  }, [monthStr, user.user_id, user.role, user.name]);
  useEffect(() => { load(); }, [load]);

  const nav = (dir) => {
    let nm = month + dir, ny = year;
    if (nm === 0) { nm = 12; ny--; }
    if (nm === 13) { nm = 1; ny++; }
    setMonth(nm); setYear(ny); setSelectedDate(null);
  };

  const monthName = new Date(year, month - 1, 1).toLocaleString("en-IN", { month: "long", year: "numeric" });

  const cells = useMemo(() => buildMonthGrid(year, month), [year, month]);

  const filtered = (events = []) => events.filter(e => filters[e.type]);
  const todayStr = `${today.getFullYear()}-${pad(today.getMonth() + 1)}-${pad(today.getDate())}`;

  const selectedEvents = selectedDate ? filtered(byDate[selectedDate] || []) : [];

  return (
    <div className="flex min-h-screen bg-slate-50">
      <Sidebar user={user} logout={logout} />
      <div className="flex-1 ml-64">
        <div className="sticky top-0 bg-white border-b border-slate-200 z-30 shadow-sm">
          <div className="px-8 py-5 flex items-center justify-between flex-wrap gap-4">
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold tracking-tight flex items-center gap-2" style={{ fontFamily: "Outfit, sans-serif" }}>
                <Bell size={24} weight="fill" className="text-amber-500" /> Alerts & Calendar
              </h1>
              <p className="text-xs text-slate-500 uppercase tracking-[0.2em] mt-1">Tasks · Reminders · Meetings — all in one view</p>
            </div>
            <div className="flex items-center gap-2">
              <button onClick={() => nav(-1)} data-testid="cal-prev" className="p-2 hover:bg-slate-100" aria-label="Previous month"><CaretLeft size={18} /></button>
              <div className="font-bold text-lg min-w-[180px] text-center" data-testid="cal-month-label">{monthName}</div>
              <button onClick={() => nav(1)} data-testid="cal-next" className="p-2 hover:bg-slate-100" aria-label="Next month"><CaretRight size={18} /></button>
              <button onClick={() => { setYear(today.getFullYear()); setMonth(today.getMonth() + 1); setSelectedDate(null); }}
                className="ml-2 rounded-none border border-slate-300 px-3 py-1.5 text-sm hover:bg-slate-50" data-testid="cal-today">Today</button>
            </div>
          </div>
        </div>

        <div className="p-8">
          {/* Summary + Filters */}
          <div className="grid grid-cols-3 gap-4 mb-4">
            {["task", "note", "meeting"].map(key => {
              const T = TYPE_STYLE[key];
              const count = totals[key === "task" ? "tasks" : key === "note" ? "notes" : "meetings"];
              const active = filters[key];
              return (
                <button key={key} data-testid={`cal-filter-${key}`}
                  onClick={() => setFilters(f => ({ ...f, [key]: !f[key] }))}
                  className={`border p-4 text-left transition ${active ? `${T.bg} ${T.border}` : 'bg-white border-slate-200 opacity-60'}`}>
                  <div className={`flex items-center gap-2 ${T.text}`}>
                    <T.Icon size={18} weight="fill" />
                    <span className="font-bold uppercase tracking-wider text-sm">{T.label}s</span>
                  </div>
                  <div className="text-3xl font-bold mt-2" style={{ fontFamily: "Outfit, sans-serif" }}>{count}</div>
                  <div className="text-xs text-slate-500 mt-1">this month · click to {active ? 'hide' : 'show'}</div>
                </button>
              );
            })}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            {/* Calendar */}
            <div className="lg:col-span-2 bg-white border border-slate-200 shadow-sm" data-testid="calendar-grid">
              <div className="grid grid-cols-7 bg-slate-50 border-b border-slate-200">
                {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map(d => (
                  <div key={d} className="px-3 py-2 text-xs uppercase tracking-[0.2em] font-bold text-slate-500 text-center border-r last:border-r-0 border-slate-200">{d}</div>
                ))}
              </div>
              {loading ? (
                <div className="flex justify-center py-20"><div className="loading-spinner" /></div>
              ) : (
                <div className="grid grid-cols-7" data-testid="calendar-cells">
                  {cells.map((cell, i) => {
                    const y = cell.date.getFullYear(), m = cell.date.getMonth() + 1, d = cell.date.getDate();
                    const dateStr = `${y}-${pad(m)}-${pad(d)}`;
                    const events = filtered(byDate[dateStr] || []);
                    const isToday = dateStr === todayStr;
                    const isSelected = selectedDate === dateStr;
                    return (
                      <button key={i} onClick={() => setSelectedDate(dateStr)}
                        data-testid={`cal-cell-${dateStr}`}
                        className={`min-h-[100px] border-r border-b border-slate-100 last:border-r-0 p-2 text-left align-top transition ${cell.inMonth ? 'bg-white' : 'bg-slate-50 opacity-50'} ${isSelected ? 'ring-2 ring-[#002FA7] ring-inset z-10 relative' : 'hover:bg-slate-50'}`}>
                        <div className={`text-sm font-medium mb-1 ${isToday ? 'bg-[#002FA7] text-white w-6 h-6 rounded-full flex items-center justify-center' : cell.inMonth ? 'text-slate-800' : 'text-slate-400'}`}>{d}</div>
                        <div className="space-y-0.5">
                          {events.slice(0, 3).map((ev, j) => {
                            const T = TYPE_STYLE[ev.type];
                            const barColor = ev.type === "task" ? PRIORITY_BAR[ev.priority_grade || "P2"] : null;
                            return (
                              <div key={`${ev.type}-${ev.ref_id}-${j}`} className={`${T.bg} ${T.text} ${T.border} border text-xs px-1.5 py-0.5 truncate flex items-center gap-1`}
                                style={barColor ? { borderLeftColor: barColor, borderLeftWidth: 3 } : undefined}>
                                <T.Icon size={10} weight="fill" className="shrink-0" />
                                <span className="truncate">{ev.title}</span>
                              </div>
                            );
                          })}
                          {events.length > 3 && <div className="text-xs text-slate-500 font-medium">+{events.length - 3} more</div>}
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Side panel: day detail */}
            <div className="bg-white border border-slate-200 shadow-sm p-5" data-testid="day-detail">
              <div className="flex items-center gap-2 mb-4">
                <Calendar size={20} weight="fill" className="text-[#002FA7]" />
                <h3 className="font-bold" style={{ fontFamily: "Outfit, sans-serif" }}>
                  {selectedDate ? new Date(selectedDate + "T00:00:00").toLocaleDateString("en-IN", { weekday: "long", day: "numeric", month: "short", year: "numeric" }) : "Select a day"}
                </h3>
              </div>
              {!selectedDate && <p className="text-sm text-slate-500">Click any day in the calendar to see its agenda.</p>}
              {selectedDate && selectedEvents.length === 0 && (
                <div className="text-sm text-slate-500 py-8 text-center">
                  <Bell size={32} weight="duotone" className="mx-auto text-slate-300 mb-2" />
                  Nothing scheduled for this day.
                </div>
              )}
              {selectedEvents.length > 0 && (
                <ul className="space-y-3">
                  {selectedEvents.map((ev, i) => {
                    const T = TYPE_STYLE[ev.type];
                    const barColor = ev.type === "task" ? PRIORITY_BAR[ev.priority_grade || "P2"] : null;
                    return (
                      <li key={`${ev.type}-${ev.ref_id}-${i}`} className={`${T.bg} ${T.border} border p-3`}
                        style={barColor ? { borderLeftColor: barColor, borderLeftWidth: 4 } : undefined}>
                        <div className="flex items-center gap-2 mb-1">
                          <T.Icon size={14} weight="fill" className={T.text} />
                          <span className={`text-xs font-bold uppercase tracking-wider ${T.text}`}>{T.label}</span>
                          {ev.time && <span className="text-xs text-slate-500 ml-auto">{ev.time}</span>}
                          {ev.priority_grade && <span className="text-xs font-bold px-1.5 py-0.5 text-white" style={{ backgroundColor: PRIORITY_BAR[ev.priority_grade] }}>{ev.priority_grade}</span>}
                        </div>
                        <div className="font-semibold text-slate-800">{ev.title}</div>
                        {ev.assigned_to && <div className="text-xs text-slate-500 mt-0.5">Assigned: {ev.assigned_to}</div>}
                        {ev.contact_name && <div className="text-xs text-slate-500 mt-0.5">With: {ev.contact_name}</div>}
                        {ev.is_shared && <span className="text-xs text-blue-700 mt-1 inline-block">Team reminder</span>}
                        {ev.dismissed && <span className="text-xs text-slate-400 mt-1 inline-block">(Dismissed)</span>}
                        <Link to={ev.link} className="text-xs text-[#002FA7] font-bold hover:underline mt-2 inline-block">Open →</Link>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Alerts;
